import { type ExternalResource, CallState } from "@b2bportal/core-types";
import type { ThunkErrorType } from "@b2bportal/core-utilities";
import { createSlice } from "@reduxjs/toolkit";
import {
  type FetchPackageFlightsFulfillResponse,
  fetchPackageFlights,
  fetchPackageFlightsRequisition,
} from "../thunks/flights/";
import { initializePackages } from "../thunks/initializePackages";

export type PackageFlightsAvailabilityState = {
  shop: ExternalResource<
    FetchPackageFlightsFulfillResponse,
    ThunkErrorType<{ error: string }>
  >;
};

export type PackageFlightsState = PackageFlightsAvailabilityState;

export const initialFlightsState: PackageFlightsState = {
  shop: { state: CallState.NotCalled },
};

export const packageFlightsSlice = createSlice({
  name: "package/flights",
  initialState: initialFlightsState,
  reducers: {
    // Explicit reset for paths that do not go through a new package search
    // (the flights up-sell). A new search clears the shop on its own below.
    resetPackageFlightsShop: (state) => {
      state.shop = initialFlightsState.shop;
    },
  },
  extraReducers: (builder) => {
    builder
      // A new package search invalidates packageStateByFareId: the departure
      // page skips its fetch whenever that map exists, so keeping it would
      // show the previous search's flights and book that bundle.
      .addCase(initializePackages.pending, (state) => {
        state.shop = initialFlightsState.shop;
      })
      .addCase(fetchPackageFlights.pending, (state) => {
        state.shop.state = CallState.InProcess;
      })
      .addCase(fetchPackageFlights.fulfilled, (state, action) => {
        state.shop.state = CallState.Success;
        state.shop.data = action.payload;
      })
      .addCase(fetchPackageFlights.rejected, (state, action) => {
        // Abandoned by the departure page on unmount (RBC-2562): the next
        // visit must shop again, so this is not a failure.
        if (action.meta.aborted) {
          state.shop = initialFlightsState.shop;
          return;
        }
        state.shop.state = CallState.Failed;
      })
      .addCase(fetchPackageFlightsRequisition.pending, (state) => {
        state.shop.state = CallState.InProcess;
      })
      .addCase(fetchPackageFlightsRequisition.fulfilled, (state, action) => {
        state.shop.state = CallState.Success;
        state.shop.data = action.payload;
      })
      .addCase(fetchPackageFlightsRequisition.rejected, (state) => {
        state.shop.state = CallState.Failed;
      });
  },
});

export const PackageFlightsActions = packageFlightsSlice.actions;
export default packageFlightsSlice.reducer;
